import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isEmpty } from "lodash";
import moment from "moment";

import HeaderSection from "./Header";
import FooterSection from "./Footer";
import InputFieldText from "Components/Common/InputFieldText";
import { getOptionValue, utilityOptions } from "common/enums";
import SelectField from "Components/Common/SelectField";
import DatePicker from "Components/Common/DatePicker";
import PaymentRequest from "Components/PaymentRequest";

import { dateFormatter } from "utils/dateFormatter";
import { onlyNumberKeys, resetFieldValue } from "utils";

import { useLazyGetBillDetailsWithConsumerNumberAndUtilityId } from "apis/UtilityConsumer/utilityConsumer";
import { usePostElectricityBillPaymentMutation } from "apis/Billing";
import { useGetMenuItemsQuery } from "apis/HomePage";

const QuickPayment = () => {
  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const { data: { data: menuItems } = {} } = useGetMenuItemsQuery();

  const [getBillDetails] =
    useLazyGetBillDetailsWithConsumerNumberAndUtilityId();

  const [
    getEncryptionForPaymentForElectricity,
    { isLoading: isElectricityBillPaying },
  ] = usePostElectricityBillPaymentMutation();

  const [isError, setIsError] = useState(false);

  const formik = useFormik({
    initialValues: {
      consumerNumber: "",
      tdsYear: moment(),
      utilityId: "",
      tdsAmount: "",
      consumerName: "",
      billNumber: "",
      billCalculationIds: [],
      billCycle: "",
      payableAmount: "",
      amount: "",
    },
    validationSchema: Yup.object({
      consumerNumber: Yup.string().required("Consumer Number is required"),
      amount: Yup.number()
        .required("Amount is required")
        .positive("Amount must be greater than 0"),
      tdsAmount: Yup.number().test("isValidTDSAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.tdsAmount > current.amount) {
          return this.createError({
            message: "TDS amount must be less than amount",
          });
        }

        return true;
      }),
      tdsYear: Yup.date().required("TDS year is required"),
    }),
  });

  const {
    values: {
      consumerNumber,
      tdsYear,
      billNumber,
      amount,
      billCalculationIds,
      utilityId,
      tdsAmount,
    },
    errors,
    handleSubmit,
    setFieldValue,
  } = formik;

  const searchBtnHandler = async () => {
    const { data } = await getBillDetails(utilityId, {
      consumerNumber,
    });
    const billDetails = data.find(
      ({ consumerNumber: currentConsumerNumber }) =>
        consumerNumber === currentConsumerNumber
    );

    if (billDetails) {
      setFieldValue(
        "billCalculationIds",
        billDetails.billingCalculationIds ?? []
      );
      setFieldValue("billCycle", billDetails?.billCycle ?? "");
      setFieldValue("consumerName", billDetails?.consumerName ?? "");
      setFieldValue("billNumber", billDetails?.billNumber ?? "");
      setFieldValue("payableAmount", billDetails?.netPayable ?? "");
      setFieldValue("amount", billDetails?.netPayable ?? "");
      setIsError(false);
    } else {
      setFieldValue("billCalculationIds", []);
      resetFieldValue(setFieldValue, [
        "billCycle",
        "consumerName",
        "billNumber",
        "payableAmount",
        "amount",
        "tdsAmount",
      ]);
      setIsError(true);
    }
  };

  const payNowBtnHandler = async () => {
    if (isEmpty(errors)) {
      try {
        return await getEncryptionForPaymentForElectricity({
          billCalculationIds,
          amount,
          tdsAmount: tdsAmount || 0,
          tdsYear: tdsYear.format("YYYY"),
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div className="d-flex flex-column justify-content-between min-vh-100">
      <div className="header-nav-wrapper nav-background">
        <div className="container-fluid">
          <HeaderSection menuItems={menuItems} isGiftInfo />
        </div>
      </div>

      <div
        className={`d-flex flex-column align-items-center ${
          !isMobileDevice ? " w-75 mx-auto" : ""
        }`}
        style={{ paddingTop: "150px", marginBottom: "10px" }}
      >
        <Grid
          container
          rowSpacing={1}
          columnSpacing={1}
          className="d-flex justify-content-start w-75"
          sx={{
            background: "#f5f5f5",
            padding: "20px",
            marginBottom: "10px",
            border: "3px solid #ced4da",
            borderRadius: "10px",
          }}
        >
          <Grid item xs={12}>
            <Typography
              className="text-center mb-3 fw-bold"
              lineHeight="1"
              variant="h5"
            >
              Bill Payment
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              label="Utility"
              name="utilityId"
              formik={formik}
              options={utilityOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Consumer Number"
              name="consumerNumber"
              formik={formik}
              placeholder="Enter Consumer Number"
            />
          </Grid>

          <Grid sx={6} md={3} lg={2} className="mt-2 ms-2">
            <Button
              disabled={
                (consumerNumber.length !== 6 && consumerNumber.length !== 7) ||
                !utilityId
              }
              variant="outlined"
              sx={{ height: "40px" }}
              onClick={searchBtnHandler}
            >
              Search
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography className="mb-3" lineHeight="1" variant="h6">
              Consumer Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Consumer Name"
              disabled
              name="consumerName"
              formik={formik}
              placeholder="Enter Consumer Name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Bill Cycle"
              disabled
              name="billCycle"
              formik={formik}
              placeholder="Enter Bill Cycle"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Bill Number"
              disabled
              name="billNumber"
              formik={formik}
              placeholder="Enter Bill Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Payable Amount"
              name="payableAmount"
              disabled
              type="number"
              formik={formik}
              placeholder="Enter Payable Amount"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className="mb-3" lineHeight="1" variant="h6">
              Payment Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Amount*"
              disabled={!billNumber}
              name="amount"
              formik={formik}
              type="number"
              placeholder="Enter Amount*"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="TDS Amount"
              disabled={!billNumber}
              name="tdsAmount"
              formik={formik}
              type="number"
              placeholder="Enter TDS Amount"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="tdsYear"
              formik={formik}
              disabled={!billNumber}
              label="Financial Year*"
              views={["year"]}
              format={dateFormatter(tdsYear)}
            />
          </Grid>

          <div
            className={`${
              !isMobileDevice ? " d-flex justify-content-between w-100" : ""
            }`}
          >
            <div>
              {isError && (
                <Typography variant="body1" color="error">
                  There is no pending bills to pay for this consumer.
                </Typography>
              )}
            </div>
            <PaymentRequest
              generateEncryption={() => {
                handleSubmit();
                return payNowBtnHandler();
              }}
              isPowerUtility={
                getOptionValue("utilityOptions", "Power") === utilityId
              }
            >
              <Button
                isElectricityBillPaying
                disabled={isElectricityBillPaying || !billNumber}
                variant="contained"
              >
                Pay Now
                {isElectricityBillPaying && <CircularProgress size={20} />}
              </Button>
            </PaymentRequest>
          </div>
        </Grid>
      </div>

      <FooterSection menuItems={menuItems} isGiftInfo />
    </div>
  );
};

export default QuickPayment;
